/**
 * w2.b — Admin Loyalty page
 *
 * Three functional sections:
 *  1. Customer search (live adminCustomersApi.search)
 *  2. Add points (service type + dollar amount → adminPointsApi.add)
 *  3. Redemption lookup (code → adminRedemptionsApi.lookup → complete)
 *
 * Plus: today's redemptions activity (last 10 via adminRedemptionsApi.today).
 *
 * #221 Wave 1 (2026-06-10): converted from inert Tailwind utilities —
 * this was the heaviest Tailwind-classed file in the app and rendered
 * as unstyled stacked text. Now on the ferro-* design system: styled
 * search + result rows, brand-tinted customer summary with the points
 * balance as the dominant number, a real 3-up tier grid with dimmed
 * unaffordable tiers ('Need N more'), success/discount cards, status
 * pills, a styled today-table that scrolls in its own container at
 * 375px, and working centered confirm dialogs (the @ferro/ui Modal
 * styles load now — the old in-flow bottom-of-page modal that locked
 * scroll is gone).
 */
import { useState, useCallback, useEffect } from 'react';
import type { ClientConfig } from '@ferro/config';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
} from '@ferro/ui';
import {
  adminCustomersApi,
  adminPointsApi,
  adminRedemptionsApi,
  readSession,
  type AddPointsResponse,
  type CompleteRedemptionResponse,
  type CreateRedemptionResponse,
  type CustomerSearchResult,
  type RedemptionLookupResponse,
} from '../lib/api.js';
import { PageErrorBoundary } from '../components/PageErrorBoundary.js';
import { LoyaltySettingsCard } from '../components/LoyaltySettingsCard.js';
import { effectiveRolesOf, rolesHavePermission } from '../lib/rbac-matrix.js';

type LoyaltyProps = {
  config: ClientConfig;
};

// #524 — the redemption rungs OFFERED, with the dollar value DERIVED from the
// tenant's configured cents-per-point (loaded at runtime). This replaces the
// old hardcoded 10¢/pt array so the desk tiers agree with the checkout leg.
const REWARD_TIER_POINTS = [100, 250, 500];
function deriveRewardTiers(
  valueCentsPerPoint: number,
): Array<{ points: number; discount: number }> {
  return REWARD_TIER_POINTS.map((points) => ({
    points,
    discount: (points * valueCentsPerPoint) / 100,
  }));
}

const SERVICE_TYPES = [
  { value: 'daycare', label: 'Daycare' },
  { value: 'boarding', label: 'Boarding' },
  { value: 'grooming', label: 'Grooming (1.5x points)' },
];

interface TodayRedemption {
  id: string;
  redemption_code: string;
  reward_tier: number;
  discount_value: number;
  status: string;
  approved_at: string | null;
  customer: { id: string; name: string };
}

function statusPillClass(status: string): string {
  if (status === 'pending') return 'ferro-status-pill ferro-status-pill--pending';
  if (status === 'completed') return 'ferro-status-pill ferro-status-pill--success';
  return 'ferro-status-pill ferro-status-pill--neutral';
}

function LoyaltyInner({ config }: LoyaltyProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;

  // #524 — the tenant's configured point value drives the desk reward tiers.
  // Starts at the 1¢ platform default and is updated by the settings card
  // once the real value loads (one source of truth). Owner-only edit.
  const [pointsValueCents, setPointsValueCents] = useState(1);
  const rewardTiers = deriveRewardTiers(pointsValueCents);
  const canEditLoyaltyValue = rolesHavePermission(
    effectiveRolesOf(readSession().staff),
    'business-config.pricing:write:all',
  );

  // ── Customer search state ────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CustomerSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // ── Selected customer ────────────────────────────────────────────────
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSearchResult | null>(null);

  // ── Points form state ────────────────────────────────────────────────
  const [dollarAmount, setDollarAmount] = useState('');
  const [serviceType, setServiceType] = useState<'daycare' | 'boarding' | 'grooming'>('daycare');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [addPointsResult, setAddPointsResult] = useState<AddPointsResponse | null>(null);

  // ── Redemption lookup state ──────────────────────────────────────────
  const [redemptionCode, setRedemptionCode] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [redemptionResult, setRedemptionResult] = useState<RedemptionLookupResponse | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [completionResult, setCompletionResult] = useState<CompleteRedemptionResponse | null>(null);

  // ── Direct redemption (no code) state ───────────────────────────────
  const [selectedTier, setSelectedTier] = useState<{ points: number; discount: number } | null>(null);
  const [showDirectModal, setShowDirectModal] = useState(false);
  const [isProcessingDirect, setIsProcessingDirect] = useState(false);
  const [directError, setDirectError] = useState<string | null>(null);
  const [directResult, setDirectResult] = useState<CreateRedemptionResponse | null>(null);

  // ── Today's redemptions ──────────────────────────────────────────────
  const [todayRedemptions, setTodayRedemptions] = useState<TodayRedemption[]>([]);
  const [isTodayLoading, setIsTodayLoading] = useState(true);

  // Load today's redemptions on mount
  useEffect(() => {
    let cancelled = false;
    setIsTodayLoading(true);
    void (async () => {
      const session = readSession();
      if (!session.token) { setIsTodayLoading(false); return; }
      // Direct fetch — adminRedemptionsApi doesn't expose a today() fn yet
      // but the server endpoint exists at GET /api/admin/loyalty/redemptions/today.
      try {
        const res = await fetch(`${apiUrl}/api/admin/redemptions/today`, {
          headers: { Authorization: `Bearer ${session.token}` },
        });
        if (!cancelled && res.ok) {
          const data = await res.json() as { redemptions: TodayRedemption[] };
          setTodayRedemptions(data.redemptions ?? []);
        }
      } catch {
        // non-fatal — today panel is informational
      } finally {
        if (!cancelled) setIsTodayLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiUrl]);

  // ── Handlers ─────────────────────────────────────────────────────────

  const handleSearch = useCallback(async () => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchError('Enter a name or phone number to search');
      return;
    }
    setIsSearching(true);
    setSearchError(null);
    setHasSearched(true);
    const result = await adminCustomersApi.search(apiUrl, q);
    setIsSearching(false);
    if (result.error) {
      setSearchError(result.error);
      setSearchResults([]);
    } else if (result.data) {
      setSearchResults(result.data.customers);
    }
  }, [apiUrl, searchQuery]);

  const handleSelectCustomer = (customer: CustomerSearchResult) => {
    setSelectedCustomer(customer);
    setSearchResults([]);
    setHasSearched(false);
    setSearchQuery('');
    setAddPointsResult(null);
    setSubmitError(null);
    setDollarAmount('');
    setServiceType('daycare');
    setDirectResult(null);
    setDirectError(null);
  };

  const handleClearSelection = () => {
    setSelectedCustomer(null);
    setDollarAmount('');
    setServiceType('daycare');
    setAddPointsResult(null);
    setSubmitError(null);
    setDirectResult(null);
    setDirectError(null);
    setSelectedTier(null);
  };

  const calculatePoints = (amount: number, service: string): number => {
    const multiplier = service === 'grooming' ? 1.5 : 1;
    return Math.floor(amount * multiplier);
  };

  const parsedAmount = parseFloat(dollarAmount) || 0;
  const previewPoints = parsedAmount > 0 ? calculatePoints(parsedAmount, serviceType) : 0;

  const handleAddPoints = async () => {
    if (!selectedCustomer) return;
    const amount = parseFloat(dollarAmount);
    if (isNaN(amount) || amount <= 0) {
      setSubmitError('Enter a valid dollar amount');
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    const result = await adminPointsApi.add(apiUrl, {
      customer_id: selectedCustomer.id,
      dollar_amount: amount,
      service_type: serviceType,
    });
    setIsSubmitting(false);
    if (result.error) {
      setSubmitError(result.error);
    } else if (result.data) {
      setAddPointsResult(result.data);
      setSelectedCustomer({ ...selectedCustomer, points_balance: result.data.customer.new_balance });
      setDollarAmount('');
      setServiceType('daycare');
    }
  };

  const handleRedemptionLookup = useCallback(async () => {
    const code = redemptionCode.trim();
    if (!code) {
      setLookupError('Enter a redemption code');
      return;
    }
    setIsLookingUp(true);
    setLookupError(null);
    setRedemptionResult(null);
    setCompletionResult(null);
    const result = await adminRedemptionsApi.lookup(apiUrl, code);
    setIsLookingUp(false);
    if (result.error) {
      setLookupError(result.error);
    } else if (result.data) {
      setRedemptionResult(result.data);
    }
  }, [apiUrl, redemptionCode]);

  const handleCompleteRedemption = async () => {
    if (!redemptionResult) return;
    setIsCompleting(true);
    const result = await adminRedemptionsApi.complete(apiUrl, redemptionResult.redemption_code);
    setIsCompleting(false);
    setShowConfirmModal(false);
    if (result.error) {
      setLookupError(result.error);
    } else if (result.data) {
      setCompletionResult(result.data);
      setRedemptionResult(null);
      setRedemptionCode('');
      // Refresh today's redemptions
      void fetch(`${apiUrl}/api/admin/redemptions/today`, {
        headers: { Authorization: `Bearer ${readSession().token ?? ''}` },
      })
        .then((r) => r.json())
        .then((d: { redemptions: TodayRedemption[] }) => setTodayRedemptions(d.redemptions ?? []))
        .catch(() => undefined);
    }
  };

  const handleClearRedemption = () => {
    setRedemptionCode('');
    setRedemptionResult(null);
    setLookupError(null);
    setCompletionResult(null);
  };

  const handleSelectTier = (tier: { points: number; discount: number }) => {
    setSelectedTier(tier);
    setShowDirectModal(true);
    setDirectError(null);
  };

  const handleProcessDirectRedemption = async () => {
    if (!selectedCustomer || !selectedTier) return;
    setIsProcessingDirect(true);
    setDirectError(null);
    const result = await adminRedemptionsApi.create(apiUrl, {
      customer_id: selectedCustomer.id,
      reward_tier: String(selectedTier.points) as '100' | '250' | '500',
    });
    setIsProcessingDirect(false);
    setShowDirectModal(false);
    if (result.error) {
      setDirectError(result.error);
    } else if (result.data) {
      setDirectResult(result.data);
      setSelectedCustomer({ ...selectedCustomer, points_balance: result.data.customer.new_balance });
      // Refresh today
      void fetch(`${apiUrl}/api/admin/redemptions/today`, {
        headers: { Authorization: `Bearer ${readSession().token ?? ''}` },
      })
        .then((r) => r.json())
        .then((d: { redemptions: TodayRedemption[] }) => setTodayRedemptions(d.redemptions ?? []))
        .catch(() => undefined);
    }
    setSelectedTier(null);
  };

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <section className="ferro-admin-page">
      <PageHeader
        title="Loyalty Points"
        subtitle="Add points, process redemptions, and manage loyalty"
      />

      {/* ─── Rewards program value (#524) ────────────────────────────── */}
      <LoyaltySettingsCard
        apiUrl={apiUrl}
        canEdit={canEditLoyaltyValue}
        onValueChanged={setPointsValueCents}
      />

      {/* ─── Customer Search ─────────────────────────────────────────── */}
      <Card title="Customer Search">
        <div className="ferro-stack">
          <div className="ferro-loyalty-search">
            <Input
              aria-label="Search customers by name or phone"
              placeholder="Search by name or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleSearch(); }}
            />
            <Button onClick={() => void handleSearch()} isLoading={isSearching}>
              Search
            </Button>
          </div>

          {searchError && <Alert variant="error">{searchError}</Alert>}

          {searchResults.length > 0 && (
            <div className="ferro-loyalty-results">
              {searchResults.map((customer) => (
                <button
                  type="button"
                  key={customer.id}
                  className="ferro-loyalty-result"
                  onClick={() => handleSelectCustomer(customer)}
                >
                  <span>
                    <span className="ferro-loyalty-result__name">{customer.name}</span>
                    <span className="ferro-loyalty-result__meta">
                      {customer.phone} · {customer.email}
                    </span>
                  </span>
                  <span className="ferro-loyalty-result__points">
                    <span className="ferro-loyalty-result__points-value">
                      {customer.points_balance.toLocaleString()}
                    </span>
                    <span className="ferro-loyalty-result__points-label">points</span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {hasSearched && searchResults.length === 0 && !searchError && !isSearching && (
            <EmptyState title={`No customers found for "${searchQuery}"`} />
          )}
        </div>
      </Card>

      {/* ─── Add Points (requires selected customer) ─────────────────── */}
      {selectedCustomer ? (
        <Card
          title="Add Points"
          subtitle="Add points for a purchase"
          headerRight={
            <Button variant="outline" size="sm" onClick={handleClearSelection}>
              Clear Selection
            </Button>
          }
        >
          <div className="ferro-stack">
            {/* Selected customer summary — balance is the dominant number */}
            <div className="ferro-loyalty-customer">
              <div>
                <p className="ferro-loyalty-result__name">{selectedCustomer.name}</p>
                <p className="ferro-loyalty-result__meta">
                  {selectedCustomer.phone} · {selectedCustomer.email}
                </p>
              </div>
              <div className="ferro-loyalty-customer__balance">
                <p className="ferro-loyalty-customer__balance-label">Balance</p>
                <p className="ferro-loyalty-customer__balance-value">
                  {selectedCustomer.points_balance.toLocaleString()} pts
                </p>
              </div>
            </div>

            {addPointsResult && (
              <Alert variant="success">
                <strong>Points added!</strong> {addPointsResult.customer.name} earned{' '}
                {addPointsResult.transaction.points_earned} points — new balance{' '}
                {addPointsResult.customer.new_balance.toLocaleString()} pts.
              </Alert>
            )}

            {submitError && <Alert variant="error">{submitError}</Alert>}

            <div className="ferro-form__row">
              <Input
                id="loyalty-dollar-amount"
                label="Dollar Amount"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={dollarAmount}
                onChange={(e) => setDollarAmount(e.target.value)}
              />
              <Select
                id="loyalty-service-type"
                label="Service Type"
                options={SERVICE_TYPES}
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value as 'daycare' | 'boarding' | 'grooming')}
              />
            </div>

            {previewPoints > 0 && (
              <div className="ferro-loyalty-preview">
                <div>
                  <p className="ferro-loyalty-result__meta">Points to be earned</p>
                  {serviceType === 'grooming' && (
                    <p className="ferro-tier-btn__note">1.5× bonus for grooming!</p>
                  )}
                </div>
                <p className="ferro-loyalty-preview__value">
                  +{previewPoints.toLocaleString()}
                </p>
              </div>
            )}

            <Button
              onClick={() => void handleAddPoints()}
              isLoading={isSubmitting}
              disabled={!dollarAmount || parsedAmount <= 0}
              block
              size="lg"
            >
              Add {previewPoints > 0 ? `${previewPoints.toLocaleString()} Points` : 'Points'}
            </Button>

            {/* Quick redemption (no code needed) */}
            <div className="ferro-subsection">
              <h3 className="ferro-subsection__title">Quick Redemption</h3>
              <p className="ferro-subsection__hint">
                Use when a customer wants to redeem at checkout without generating a code in the app.
              </p>

              <div className="ferro-stack">
                {directError && <Alert variant="error">{directError}</Alert>}

                {directResult && (
                  <div className="ferro-loyalty-discount" role="status">
                    <p className="ferro-loyalty-discount__title">
                      Redemption complete for {directResult.customer.name}
                    </p>
                    <p className="ferro-loyalty-discount__label">Apply discount:</p>
                    <p className="ferro-loyalty-discount__value">
                      ${directResult.discount_to_apply}
                    </p>
                    <p className="ferro-loyalty-discount__meta">
                      {directResult.redemption.reward_tier} points redeemed
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDirectResult(null)}
                    >
                      Dismiss
                    </Button>
                  </div>
                )}

                <div className="ferro-tier-grid">
                  {rewardTiers.map((tier) => {
                    const canAfford = selectedCustomer.points_balance >= tier.points;
                    const needed = tier.points - selectedCustomer.points_balance;
                    return (
                      <button
                        type="button"
                        key={tier.points}
                        onClick={() => canAfford && handleSelectTier(tier)}
                        disabled={!canAfford}
                        className="ferro-tier-btn"
                      >
                        <span className="ferro-tier-btn__amount">${tier.discount.toFixed(2)}</span>
                        <span className="ferro-tier-btn__points">{tier.points} pts</span>
                        <span className="ferro-tier-btn__note">
                          {canAfford ? 'Available' : `Need ${needed} more`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </Card>
      ) : (
        <Card>
          <EmptyState
            title="No customer selected"
            description="Search for a customer above to add points."
          />
        </Card>
      )}

      {/* ─── Redemption Code Lookup ───────────────────────────────────── */}
      <Card title="Customer Has a Code" subtitle="Look up and complete a redemption code presented by the customer.">
        <div className="ferro-stack">
          <div className="ferro-loyalty-search">
            <Input
              aria-label="Redemption code"
              placeholder="Enter code (e.g. RD-ABC123)..."
              value={redemptionCode}
              onChange={(e) => setRedemptionCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleRedemptionLookup(); }}
            />
            <div className="ferro-form__actions">
              <Button onClick={() => void handleRedemptionLookup()} isLoading={isLookingUp}>
                Look Up
              </Button>
              {(redemptionResult ?? completionResult) && (
                <Button variant="outline" onClick={handleClearRedemption}>Clear</Button>
              )}
            </div>
          </div>

          {lookupError && <Alert variant="error">{lookupError}</Alert>}

          {completionResult && (
            <div className="ferro-loyalty-discount" role="status">
              <p className="ferro-loyalty-discount__title">
                Completed for {completionResult.customer.name}
              </p>
              <p className="ferro-loyalty-discount__label">Apply discount:</p>
              <p className="ferro-loyalty-discount__value">
                ${completionResult.discount_to_apply}
              </p>
              <p className="ferro-loyalty-discount__meta">
                {completionResult.redemption.reward_tier} points redeemed
              </p>
            </div>
          )}

          {redemptionResult && (
            <div
              className={`ferro-loyalty-code${
                redemptionResult.status === 'pending' ? ' ferro-loyalty-code--pending' : ''
              }`}
            >
              <div className="ferro-loyalty-code__head">
                <div>
                  <p className="ferro-pair__label">Code</p>
                  <p className="ferro-mono-strong">{redemptionResult.redemption_code}</p>
                </div>
                <span className={statusPillClass(redemptionResult.status)}>
                  {redemptionResult.status}
                </span>
              </div>

              <div className="ferro-pair-grid">
                <div>
                  <p className="ferro-pair__label">Customer</p>
                  <p className="ferro-pair__value">{redemptionResult.customer.name}</p>
                  <p className="ferro-pair__sub">{redemptionResult.customer.phone}</p>
                </div>
                <div>
                  <p className="ferro-pair__label">Discount Value</p>
                  <p className="ferro-pair__value ferro-pair__value--accent">
                    ${redemptionResult.discount_value}
                  </p>
                  <p className="ferro-pair__sub">{redemptionResult.reward_tier} points</p>
                </div>
                <div>
                  <p className="ferro-pair__label">Customer Balance</p>
                  <p className="ferro-pair__value">
                    {redemptionResult.customer.points_balance.toLocaleString()} pts
                  </p>
                </div>
                <div>
                  <p className="ferro-pair__label">Requested</p>
                  <p className="ferro-pair__value">
                    {new Date(redemptionResult.created_at).toLocaleString()}
                  </p>
                </div>
              </div>

              {redemptionResult.status === 'pending' && (
                <Button onClick={() => setShowConfirmModal(true)} block size="lg">
                  Complete Redemption
                </Button>
              )}

              {redemptionResult.status === 'completed' && redemptionResult.approved_at && (
                <p className="ferro-centered-note">
                  Completed {new Date(redemptionResult.approved_at).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {!redemptionResult && !completionResult && !lookupError && (
            <p className="ferro-centered-note">Enter a code above to look it up.</p>
          )}
        </div>
      </Card>

      {/* ─── Today's Redemptions ─────────────────────────────────────── */}
      <Card title="Today's Redemptions" subtitle="Last 10 completed today." noPadding>
        {isTodayLoading ? (
          // `block` mode = @ferro/ui's neutral full-width centered loading
          // row — not the borrowed .ferro-insight-empty container (that
          // class belongs to the dashboard InsightsPanel empty state).
          <Spinner block label="Loading today's redemptions" />
        ) : todayRedemptions.length === 0 ? (
          <EmptyState title="No redemptions completed today" />
        ) : (
          <div className="ferro-table-scroll">
            <table className="ferro-admin-table" aria-label="Today's redemptions">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Code</th>
                  <th>Points</th>
                  <th>Discount</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {todayRedemptions.map((r) => (
                  <tr key={r.id}>
                    <td className="ferro-admin-table__primary">{r.customer.name}</td>
                    <td className="ferro-mono">{r.redemption_code}</td>
                    <td>{r.reward_tier}</td>
                    <td className="ferro-admin-table__primary">${r.discount_value}</td>
                    <td>
                      {r.approved_at ? new Date(r.approved_at).toLocaleTimeString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ─── Confirm complete modal ───────────────────────────────────── */}
      <Modal isOpen={showConfirmModal} onClose={() => setShowConfirmModal(false)} title="Complete Redemption">
        {redemptionResult && (
          <div>
            <p className="ferro-subsection__hint">Complete this redemption?</p>
            <div className="ferro-confirm-list">
              <div className="ferro-confirm-list__row">
                <span className="ferro-confirm-list__label">Customer</span>
                <span className="ferro-confirm-list__value">{redemptionResult.customer.name}</span>
              </div>
              <div className="ferro-confirm-list__row">
                <span className="ferro-confirm-list__label">Code</span>
                <span className="ferro-confirm-list__value ferro-mono">
                  {redemptionResult.redemption_code}
                </span>
              </div>
              <div className="ferro-confirm-list__row">
                <span className="ferro-confirm-list__label">Points deducted</span>
                <span className="ferro-confirm-list__value ferro-confirm-list__value--debit">
                  -{redemptionResult.reward_tier}
                </span>
              </div>
              <div className="ferro-confirm-list__row ferro-confirm-list__row--total">
                <span className="ferro-confirm-list__label">Discount to apply</span>
                <span className="ferro-confirm-list__value ferro-confirm-list__value--total">
                  ${redemptionResult.discount_value}
                </span>
              </div>
            </div>
            <div className="ferro-form__actions ferro-form__actions--fill">
              <Button variant="outline" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
              <Button onClick={() => void handleCompleteRedemption()} isLoading={isCompleting}>
                Complete Redemption
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── Direct redemption confirm modal ─────────────────────────── */}
      <Modal isOpen={showDirectModal} onClose={() => setShowDirectModal(false)} title="Process Redemption">
        {selectedCustomer && selectedTier && (
          <div>
            <p className="ferro-subsection__hint">Process this redemption?</p>
            <div className="ferro-confirm-list">
              <div className="ferro-confirm-list__row">
                <span className="ferro-confirm-list__label">Customer</span>
                <span className="ferro-confirm-list__value">{selectedCustomer.name}</span>
              </div>
              <div className="ferro-confirm-list__row">
                <span className="ferro-confirm-list__label">Current balance</span>
                <span className="ferro-confirm-list__value">
                  {selectedCustomer.points_balance.toLocaleString()} pts
                </span>
              </div>
              <div className="ferro-confirm-list__row">
                <span className="ferro-confirm-list__label">Points deducted</span>
                <span className="ferro-confirm-list__value ferro-confirm-list__value--debit">
                  -{selectedTier.points}
                </span>
              </div>
              <div className="ferro-confirm-list__row">
                <span className="ferro-confirm-list__label">Balance after</span>
                <span className="ferro-confirm-list__value">
                  {(selectedCustomer.points_balance - selectedTier.points).toLocaleString()} pts
                </span>
              </div>
              <div className="ferro-confirm-list__row ferro-confirm-list__row--total">
                <span className="ferro-confirm-list__label">Discount to apply</span>
                <span className="ferro-confirm-list__value ferro-confirm-list__value--total">
                  ${selectedTier.discount.toFixed(2)}
                </span>
              </div>
            </div>
            <div className="ferro-form__actions ferro-form__actions--fill">
              <Button variant="outline" onClick={() => setShowDirectModal(false)}>Cancel</Button>
              <Button onClick={() => void handleProcessDirectRedemption()} isLoading={isProcessingDirect}>
                Process Redemption
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
}

export function Loyalty({ config }: LoyaltyProps): JSX.Element {
  return (
    <PageErrorBoundary>
      <LoyaltyInner config={config} />
    </PageErrorBoundary>
  );
}
